import { z } from 'zod'

/**
 * What a performer plays. A selection is a SET of parts: a singer who also plays guitar picks both.
 * "Full chart" cannot be combined with other parts.
 *
 * See docs/ux/2026-10-12-performance-roles-and-notes-ux.md.
 */
export const performanceParts = ['vocals', 'guitar', 'bass', 'keys', 'drums', 'full'] as const
export const performancePartSchema = z.enum(performanceParts)
export type PerformancePart = z.infer<typeof performancePartSchema>

export function isPerformancePart(value: unknown): value is PerformancePart {
  return performancePartSchema.safeParse(value).success
}

export const performancePartLabels: Record<PerformancePart, string> = {
  vocals: 'Vocals',
  guitar: 'Guitar',
  bass: 'Bass',
  keys: 'Keys',
  drums: 'Drums',
  full: 'Full chart',
}

const byCanonicalOrder = (a: PerformancePart, b: PerformancePart) =>
  performanceParts.indexOf(a) - performanceParts.indexOf(b)

/**
 * Read a stored part set. Stored sets are plain strings, so a part this build does not know degrades
 * instead of invalidating the user's config. Unknown values are dropped, duplicates removed, the order
 * made canonical, and Full chart dropped when other parts are present.
 *
 * An empty result is `undefined`, so resolution falls through to the next level (band → default → picker).
 */
export const normalizeParts = (stored: unknown): PerformancePart[] | undefined => {
  if (!Array.isArray(stored)) {
    return undefined
  }
  const parts = [...new Set(stored.filter(isPerformancePart))].sort(byCanonicalOrder)
  const withoutFull = parts.filter(part => part !== 'full')
  const result = withoutFull.length > 0 ? withoutFull : parts
  return result.length > 0 ? result : undefined
}

/**
 * Toggle one part in a selection, as the Perform part menu does. Full chart is exclusive, and the
 * result is never empty: removing the last part leaves Full chart.
 */
export const togglePart = (current: readonly PerformancePart[], part: PerformancePart): PerformancePart[] => {
  if (part === 'full') {
    return ['full']
  }
  const others = current.filter(p => p !== 'full')
  const next = others.includes(part) ? others.filter(p => p !== part) : [...others, part]
  return normalizeParts(next) ?? ['full']
}

/** Short label for the header: "Guitar", "Vocals + Guitar", or "3 parts" for three or more. */
export const partsLabel = (parts: readonly PerformancePart[]) =>
  parts.length > 2 ? `${parts.length} parts` : parts.map(part => performancePartLabels[part]).join(' + ')

/** Every part by name, for accessible names and the default-parts notice: "Vocals, Guitar and Bass". */
export const partsList = (parts: readonly PerformancePart[]) => {
  const labels = parts.map(part => performancePartLabels[part])
  const last = labels.pop()
  if (last === undefined) {
    return ''
  }
  return labels.length > 0 ? `${labels.join(', ')} and ${last}` : last
}
